import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { Button, Input, Card, DataTable, Modal } from '../../components/ui';
import { ConfirmModal } from '../../components/ui/Modal';

function MacrocategorieAdminPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const [macrocategorie, setMacrocategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({ label: '' });

  useEffect(() => {
    loadMacrocategorie();
  }, []);

  const loadMacrocategorie = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/admin/dataverse/macrocategoria-options');
      setMacrocategorie(data.options || []);
    } catch (error) {
      console.error('Error loading macrocategorie:', error);
      showToast('Errore nel caricamento delle macrocategorie', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({ label: '' });
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({ label: item.label || '' });
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.label.trim()) {
      showToast('Il nome della macrocategoria è obbligatorio', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await apiRequest(`/admin/dataverse/macrocategoria-options/${editingItem.value}`, {
          method: 'PUT',
          body: JSON.stringify({ label: formData.label.trim() })
        });
        showToast('Macrocategoria aggiornata con successo', 'success');
      } else {
        await apiRequest('/admin/dataverse/macrocategoria-options', {
          method: 'POST',
          body: JSON.stringify({ label: formData.label.trim() })
        });
        showToast('Macrocategoria creata con successo', 'success');
      }

      setModalOpen(false);
      loadMacrocategorie();
    } catch (error) {
      console.error('Error saving macrocategoria:', error);
      if (error.message?.includes('Conflict') || error.message?.includes('already exists')) {
        showToast('Questa macrocategoria esiste già', 'error');
      } else {
        showToast(error.message || 'Errore durante il salvataggio', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await apiRequest(`/admin/dataverse/macrocategoria-options/${itemToDelete.value}`, {
        method: 'DELETE'
      });
      showToast('Macrocategoria eliminata con successo', 'success');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadMacrocategorie();
    } catch (error) {
      console.error('Error deleting macrocategoria:', error);
      if (error.message?.includes('Conflict') || error.message?.includes('tipologie')) {
        showToast('Impossibile eliminare: ci sono tipologie che usano questa macrocategoria', 'error');
      } else {
        showToast(error.message || 'Errore durante l\'eliminazione', 'error');
      }
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const columns = [
    {
      key: 'label',
      label: 'Nome Macrocategoria',
      sortable: true,
      render: (row) => (
        <div className="font-medium text-gray-900">{row.label}</div>
      )
    },
    {
      key: 'value',
      label: 'Codice',
      sortable: true,
      render: (row) => (
        <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {row.value}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Azioni',
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleOpenEdit(row); }}
            className="p-2 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded"
            title="Modifica"
          >
            ✏️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); confirmDelete(row); }}
            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
            title="Elimina"
          >
            🗑️
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <a href="/admin" className="hover:text-primary-600">Amministrazione</a>
            <span>/</span>
            <span>Macrocategorie</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Macrocategorie</h1>
          <p className="text-gray-500 mt-1">
            Gestisci le macrocategorie per tipologie intervento e fornitura
          </p>
        </div>

        <Button variant="primary" onClick={handleOpenCreate}>
          + Nuova Macrocategoria
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <span className="text-2xl">🏷️</span>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{macrocategorie.length}</div>
              <div className="text-sm text-gray-500">Totale Macrocategorie</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <span className="text-xl">💡</span>
          <div className="text-sm text-blue-800">
            <p>
              Le macrocategorie raggruppano le <strong>tipologie intervento</strong> e le <strong>tipologie fornitura</strong> in categorie principali
              (es. Impianti elettrici, Antincendio, Edilizia, ecc.).
            </p>
            <p className="mt-1">
              Le macrocategorie sono condivise tra tipologie intervento e tipologie fornitura.
              Prima di eliminare una macrocategoria, assicurati che non sia usata da nessuna tipologia.
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <DataTable
          data={macrocategorie}
          columns={columns}
          loading={loading}
          searchable
          searchPlaceholder="Cerca macrocategoria..."
          emptyMessage="Nessuna macrocategoria trovata"
          onRowClick={handleOpenEdit}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? 'Modifica Macrocategoria' : 'Nuova Macrocategoria'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome macrocategoria *"
            name="label"
            value={formData.label}
            onChange={handleChange}
            placeholder="es. Impianti elettrici, Antincendio, Edilizia..."
            maxLength={200}
            autoFocus
          />

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || !formData.label.trim()}
            >
              {saving ? 'Salvataggio...' : (editingItem ? 'Salva' : 'Crea Macrocategoria')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setItemToDelete(null); }}
        onConfirm={handleDelete}
        title="Elimina Macrocategoria"
        message={
          <>
            Sei sicuro di voler eliminare la macrocategoria <strong>"{itemToDelete?.label}"</strong>?
            <br /><br />
            <span className="text-amber-600">
              ⚠️ Se ci sono tipologie intervento o fornitura che usano questa macrocategoria, l'eliminazione non sarà possibile.
            </span>
          </>
        }
        confirmLabel="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default MacrocategorieAdminPage;
